/**
 * Funções padronizadas para formatação de datas independente do locale do sistema
 * Resolve problemas de internacionalização onde usuários com sistema em inglês
 * têm datas incorretas enviadas ao backend
 */

/**
 * Formata data para exibição sempre no padrão brasileiro DD/MM/YYYY
 * @param date - String de data ISO ou objeto Date
 * @returns String formatada no padrão DD/MM/YYYY
 */
export const formatDateToBrazilian = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Força o locale pt-BR para garantir formato consistente
  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Formata data para envio ao backend sempre no padrão ISO (YYYY-MM-DD)
 * @param date - String de data no formato DD/MM/YYYY ou YYYY-MM-DD
 * @returns String no formato ISO YYYY-MM-DD para backend
 */
export const formatDateToISO = (date: string): string => {
  let dateObj: Date

  // Detecta se é formato brasileiro DD/MM/YYYY ou DD/MM
  if (date.includes('/') && date.split('/')[0].length <= 2) {
    const parts = date.split('/')
    const day = parseInt(parts[0])
    const month = parseInt(parts[1])
    const year = parts[2] ? parseInt(parts[2]) : new Date().getFullYear()

    dateObj = new Date(year, month - 1, day)
  } else {
    // Formato ISO ou americano
    dateObj = new Date(date)
  }

  // Garante formato YYYY-MM-DD
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Formata data e hora para backend sempre no padrão ISO
 * Substitui as funções formattedDate que usam toLocaleTimeString
 * @param date - String de data
 * @returns String ISO completa YYYY-MM-DDTHH:mm:ss.sssZ
 */
export const formatDateTimeToISO = (date: string): string => {
  if (!date) {
    return new Date().toISOString()
  }

  // Cria objeto Date a partir da string de data
  const dateObj = new Date(formatDateToISO(date))

  // Adiciona horário atual padronizado (00:00:00 UTC)
  // Evita problemas de timezone e locale
  dateObj.setUTCHours(0, 0, 0, 0)

  return dateObj.toISOString()
}

/**
 * Formata data com horário atual para backend
 * Versão corrigida das funções formattedDate problemáticas
 * @param date - String de data
 * @returns String ISO com horário atual
 */
export const formatDateWithCurrentTime = (date: string): string => {
  if (!date) {
    return new Date().toISOString()
  }

  let year: number, month: number, day: number

  // Parse da data de entrada
  if (date.includes('/') && date.split('/')[0].length <= 2) {
    const parts = date.split('/')
    day = parseInt(parts[0])
    month = parseInt(parts[1])
    year = parts[2] ? parseInt(parts[2]) : new Date().getFullYear()
  } else {
    // Se é formato ISO (YYYY-MM-DD), parse manualmente para evitar timezone issues
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = date.split('-')
      year = parseInt(parts[0])
      month = parseInt(parts[1])
      day = parseInt(parts[2])
    } else {
      const tempDate = new Date(date)
      year = tempDate.getFullYear()
      month = tempDate.getMonth() + 1
      day = tempDate.getDate()
    }
  }

  // Usa horário atual mas garante que a data não mude
  const now = new Date()

  // Usa componentes UTC para garantir consistência
  const utcHours = now.getUTCHours()
  const utcMinutes = now.getUTCMinutes()
  const utcSeconds = now.getUTCSeconds()
  const utcMilliseconds = now.getUTCMilliseconds()

  // Cria string ISO manualmente para evitar problemas de timezone
  const isoString = `${year}-${String(month).padStart(2, '0')}-${String(
    day
  ).padStart(2, '0')}T${String(utcHours).padStart(2, '0')}:${String(
    utcMinutes
  ).padStart(2, '0')}:${String(utcSeconds).padStart(2, '0')}.${String(
    utcMilliseconds
  ).padStart(3, '0')}Z`

  return isoString
}

/**
 * Converte timestamp ou string de data para formato brasileiro
 * Para uso em exibição de dados (ex: KPIs, tabelas)
 * @param date - String ISO, timestamp ou Date
 * @returns String DD/MM/YYYY
 */
export const displayDateBrazilian = (date: string | number | Date): string => {
  const dateObj = new Date(date)

  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Converte timestamp para formato brasileiro com hora
 * @param date - String ISO, timestamp ou Date
 * @returns String DD/MM/YYYY HH:mm:ss
 */
export const displayDateTimeBrazilian = (
  date: string | number | Date
): string => {
  const dateObj = new Date(date)

  return dateObj.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}
